import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, forkJoin, map, of } from 'rxjs';
import {
  ArrowRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Eye,
  FileCheck,
  FileClock,
  FileDown,
  FilePlus,
  FileText,
  Link2,
  LucideAngularModule,
  Pencil,
  RefreshCw,
  Search,
  ShoppingCart,
  Trash2,
  User,
  X,
} from 'lucide-angular';
import { Contract, Loa, LoaSchedule, Vendor } from '../../../core/models/domain.models';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../shared/ui/toast.service';
import { ContractsService } from '../../contract/services/contracts.service';
import { VendorsService } from '../../vendor/services/vendors.service';
import { LoaService } from '../services/loa.service';

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];

const STATE_REGION_MAP: Record<string, string> = {
  Delhi: 'NR',
  Haryana: 'NR',
  Punjab: 'NR',
  Rajasthan: 'NR',
  'Uttar Pradesh': 'NR',
  Gujarat: 'WR',
  Maharashtra: 'WR',
  Goa: 'WR',
  'Madhya Pradesh': 'WR',
  Chhattisgarh: 'WR',
  Bihar: 'ER',
  Jharkhand: 'ER',
  Odisha: 'ER',
  'West Bengal': 'ER',
  Karnataka: 'SR',
  Kerala: 'SR',
  'Tamil Nadu': 'SR',
  Telangana: 'SR',
  'Andhra Pradesh': 'SR',
};

type FilterMode = 'all' | 'Buy' | 'Sell' | 'Banking';
type SummaryFilterMode = 'all' | 'total' | 'draft' | 'approved' | 'pending_linking' | 'pending';

const SUMMARY_FILTER_BY_LABEL: Record<string, SummaryFilterMode> = {
  'Total LOA Generated': 'total',
  'Draft LOA': 'draft',
  'Approved LOA': 'approved',
  'Pending Contract Linking': 'pending_linking',
  'Pending LOA': 'pending',
};

@Component({
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  styles: [
    `
      :host {
        display: block;
      }

      .loa-page {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .loa-shell {
        background: linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%);
        border: 1px solid #dbe7ff;
        border-radius: 20px;
        box-shadow: 0 10px 30px rgba(33, 76, 170, 0.08);
      }

      .page-title-lite {
        margin: 0;
        font-size: 2rem;
        font-weight: 700;
        letter-spacing: -0.03em;
        color: #24334f;
      }

      .title-badge {
        width: 42px;
        height: 42px;
        border-radius: 14px;
        background: linear-gradient(135deg, #eef4ff 0%, #dbe8ff 100%);
        color: #3d66da;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        box-shadow: inset 0 0 0 1px rgba(61, 102, 218, 0.14);
      }

      .summary-card {
        border: 1px solid #3563e9;
        border-radius: 16px;
        background: #fff;
        padding: 0.95rem 1rem 0.8rem;
        box-shadow: 0 12px 28px rgba(37, 82, 185, 0.08);
      }

      .summary-card.clickable,
      .summary-segment.clickable {
        cursor: pointer;
      }

      .summary-card.clickable:hover {
        box-shadow: 0 16px 34px rgba(37, 82, 185, 0.12);
      }

      .summary-segment.clickable:hover {
        filter: brightness(0.95);
      }

      .summary-card-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 1rem;
        margin-bottom: 0.6rem;
      }

      .summary-label {
        color: #2a344b;
        font-size: 0.92rem;
        font-weight: 600;
      }

      .summary-value {
        color: #202636;
        font-size: 2.1rem;
        line-height: 1;
        font-weight: 700;
      }

      .summary-track {
        overflow: hidden;
        border-radius: 999px;
        background: #e9eef7;
        height: 0.9rem;
        display: flex;
        box-shadow: inset 0 2px 8px rgba(50, 70, 120, 0.12);
      }

      .summary-segment {
        height: 100%;
        min-width: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 0.78rem;
        font-weight: 700;
      }

      .summary-segment.sell {
        background: #e6312c;
      }

      .summary-segment.buy {
        background: #2d9f00;
      }

      .summary-segment.banking {
        background: #347ae9;
      }

      .panel-card {
        background: #fff;
        border: 1px solid #e0ebff;
        border-radius: 20px;
        box-shadow: 0 12px 30px rgba(37, 82, 185, 0.08);
      }

      .quick-group {
        display: inline-flex;
        flex-wrap: nowrap;
        gap: 0.5rem;
      }

      .toolbar-row {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 1rem;
        flex-wrap: nowrap;
      }

      .toolbar-filters {
        display: flex;
        align-items: flex-end;
        gap: 0.75rem;
        flex-wrap: nowrap;
        margin-left: auto;
        justify-content: flex-end;
      }

      .search-icon {
        position: absolute;
        left: 0.9rem;
        top: 50%;
        transform: translateY(-50%);
        color: #71809d;
      }

      .search-input {
        min-height: 44px;
        min-width: 260px;
        border-radius: 10px;
        border-color: #d6deef;
        box-shadow: none;
        padding-left: 2.5rem;
      }

      .quick-btn {
        border-radius: 999px;
        min-height: 44px;
        padding: 0.65rem 1.2rem;
        border: 1px solid #5d91f5;
        background: #fff;
        color: #2a64d7;
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        box-shadow: 0 6px 18px rgba(39, 87, 195, 0.08);
      }

      .quick-btn.active {
        background: linear-gradient(180deg, #185dcb 0%, #0f4db4 100%);
        border-color: #0f4db4;
        color: #fff;
      }

      .filter-field label {
        color: #44506a;
        font-size: 0.78rem;
        font-weight: 700;
      }

      .filter-field .form-select,
      .filter-field .form-control {
        min-height: 44px;
        border-radius: 10px;
        border-color: #d6deef;
        box-shadow: none;
      }

      .filter-action {
        min-height: 44px;
        border-radius: 10px;
        padding-inline: 1.1rem;
        font-weight: 700;
      }

      .table-wrap {
        border: 1px solid #dde6f5;
        border-radius: 14px;
        overflow: hidden;
      }

      .table-head th {
        background: #eef1f5;
        border-color: #d9e1ec;
        color: #364153;
        font-size: 0.88rem;
        font-weight: 700;
        white-space: nowrap;
      }

      tbody td {
        border-color: #e4ebf4;
        vertical-align: middle;
        color: #33415c;
      }

      .entity-link {
        color: #5578ff;
        font-weight: 500;
        cursor: pointer;
        text-decoration: none;
      }

      .entity-link:hover {
        text-decoration: underline;
      }

      .status-pill {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border-radius: 999px;
        background: #46b10f;
        color: #fff;
        font-size: 13px;
        font-weight: 700;
        padding: 3px 12px 3px 8px;
      }

      .metric-pill {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 999px;
        padding: 3px 12px;
        font-size: 13px;
        font-weight: 700;
        min-width: 72px;
      }

      .metric-pill.loa-type {
        background: #eef4ff;
        color: #245ed9;
      }

      .metric-pill.loa-type.buy {
        background: #eef9ef;
        color: #2b8a3e;
      }

      .metric-pill.loa-type.sell {
        background: #fff1f2;
        color: #c92a2a;
      }

      .metric-pill.loa-type.banking {
        background: #eef4ff;
        color: #245ed9;
      }

      .metric-pill.day-count {
        background: #fff4e8;
        color: #b86a00;
      }

      .metric-pill.quantum {
        background: #eef9ef;
        color: #2b8a3e;
      }

      .status-pill.pending {
        background: #d39c00;
      }

      .status-pill.draft {
        background: #7a8594;
      }

      .status-pill.rejected {
        background: #b82d2d;
      }

      .action-eye {
        background: #e8f7ff;
        border: 1px solid #c8e3f3;
        border-radius: 8px;
        width: 34px;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .action-edit {
        background: #fff7e8;
        border: 1px solid #f0ddb2;
        border-radius: 8px;
        width: 34px;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #a66a00;
      }

      .action-delete {
        background: #fff1f2;
        border: 1px solid #f0c7cc;
        border-radius: 8px;
        width: 34px;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #b42318;
      }

      .action-edit:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .action-delete:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .pager-btn {
        background: #e4eefc;
        border: none;
        width: 34px;
        height: 34px;
        border-radius: 999px;
        color: #1e4f98;
      }

      .pager-btn:disabled {
        opacity: 0.5;
      }

      .pagination-shell {
        display: inline-flex;
        align-items: center;
        gap: 0.7rem;
        flex-wrap: wrap;
      }

      .pager-meta,
      .page-indicator {
        color: #5c6b85;
        font-size: 0.9rem;
        font-weight: 600;
      }

      .page-indicator.current {
        min-width: 18px;
        text-align: center;
      }

      .create-btn {
        min-height: 42px;
        border-radius: 999px;
        font-weight: 700;
        border: none;
        box-shadow: 0 10px 20px rgba(31, 73, 162, 0.16);
      }

      .create-btn.buy {
        background: #3ea304;
        color: #fff;
      }

      .create-btn.sell {
        background: #dc3545;
        color: #fff;
      }

      .create-btn.banking {
        background: #0d6efd;
        color: #fff;
      }

      .create-btn.export {
        background: #f8f9fa;
        color: #1f3f75;
        border: 1px solid #cfd9ec;
        box-shadow: none;
      }

      .export-excel-btn {
        width: 42px;
        min-width: 42px;
        padding: 0;
        justify-content: center;
        background: #effaf3;
        border-color: #bfe3ca;
        color: #1d6f42;
      }

      .export-excel-btn:hover {
        background: #e1f4e8;
        border-color: #9fcfb0;
      }

      .excel-icon {
        color: #1d6f42;
      }

      .page-size-select {
        width: 110px;
        border-radius: 999px;
      }

      @media (max-width: 991.98px) {
        .page-title-lite {
          font-size: 1.6rem;
        }

        .summary-value {
          font-size: 1.7rem;
        }

        .toolbar-row,
        .toolbar-filters,
        .quick-group {
          flex-wrap: wrap;
        }
      }
    `,
  ],
  templateUrl: './loa-list.page.html',
})
export class LoaListPage {
  protected readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loaService = inject(LoaService);
  private readonly vendorsService = inject(VendorsService);
  private readonly contractsService = inject(ContractsService);
  private readonly auth = inject(AuthService);
  private readonly toast = inject(ToastService);

  protected readonly FileTextIcon = FileText;
  protected readonly ShoppingCartIcon = ShoppingCart;
  protected readonly UserIcon = User;
  protected readonly RefreshCwIcon = RefreshCw;
  protected readonly SearchIcon = Search;
  protected readonly ArrowRightIcon = ArrowRight;
  protected readonly XIcon = X;
  protected readonly EyeIcon = Eye;
  protected readonly PencilIcon = Pencil;
  protected readonly Trash2Icon = Trash2;
  protected readonly CircleCheckIcon = CircleCheck;
  protected readonly isVendorUser = computed(() => this.auth.user()?.role === 'vendor');
  protected readonly userRoleKey = computed(() =>
    (this.auth.user()?.role || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_'),
  );
  protected readonly FileDownIcon = FileDown;
  protected readonly ChevronsLeftIcon = ChevronsLeft;
  protected readonly ChevronsRightIcon = ChevronsRight;
  protected readonly ChevronLeftIcon = ChevronLeft;
  protected readonly ChevronRightIcon = ChevronRight;

  protected readonly pageSizes = PAGE_SIZE_OPTIONS;

  protected readonly allLoas = signal<Loa[]>([]);
  protected readonly scheduleMap = signal<Record<string, LoaSchedule[]>>({});
  protected readonly vendors = signal<Vendor[]>([]);
  protected readonly contracts = signal<Contract[]>([]);
  protected readonly categoryMode = signal<FilterMode>(this.defaultCategoryMode());
  protected readonly draftStatusFilter = signal('all');
  protected readonly draftSearch = signal('');
  protected readonly draftFromDate = signal('');
  protected readonly draftToDate = signal('');
  protected readonly statusFilter = signal('all');
  protected readonly searchQuery = signal('');
  protected readonly fromDate = signal('');
  protected readonly toDate = signal('');
  protected readonly summaryFilter = signal<SummaryFilterMode>('all');
  protected readonly currentPage = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly loading = signal(true);
  protected readonly deletingLoaId = signal<string | null>(null);
  private loadGeneration = 0;
  private pendingLoadTasks = 0;

  protected readonly vendorMap = computed(() => {
    const map: Record<string, string> = {};
    this.vendors().forEach((vendor) => {
      map[vendor.id] = vendor.vendor_name;
    });
    return map;
  });

  protected readonly vendorDetailsMap = computed(() => {
    const map: Record<string, Vendor> = {};
    this.vendors().forEach((vendor) => {
      map[vendor.id] = vendor;
    });
    return map;
  });

  protected readonly linkedLoaIds = computed(() => {
    const ids = new Set<string>();
    this.contracts().forEach((contract) => {
      (contract.party_details || []).forEach((party) => {
        const loaId = (party.loa_id || '').trim();
        if (loaId) ids.add(loaId);
      });
    });
    return ids;
  });

  protected readonly summaryTiles = computed(() => {
    const loas = this.allLoas();
    return [
      this.makeTile('Total LOA Generated', loas, FileText),
      this.makeTile('Draft LOA', loas.filter((item) => this.isDraftLoa(item)), FilePlus),
      this.makeTile('Approved LOA', loas.filter((item) => this.isApprovedLoa(item)), FileCheck),
      this.makeTile('Pending Contract Linking', loas.filter((item) => this.isPendingContractLinking(item)), Link2),
      this.makeTile('Pending LOA', loas.filter((item) => this.isPendingApprovalLoa(item)), FileClock),
    ];
  });

  protected readonly filteredLoas = computed(() => {
    const categoryMode = this.categoryMode();
    const status = this.statusFilter();
    const search = this.searchQuery().trim().toLowerCase();
    const from = this.fromDate();
    const to = this.toDate();
    const summaryFilter = this.summaryFilter();

    return this.allLoas().filter((loa) => {
      if (categoryMode !== 'all' && loa.category !== categoryMode) return false;
      if (status !== 'all' && this.normalizedLoaWorkflowStatus(loa) !== status) return false;
      if (search) {
        const loaNumber = (loa.loa_number || '').toLowerCase();
        const entityName = (this.vendorMap()[loa.vendor_id] || '').toLowerCase();
        if (!loaNumber.includes(search) && !entityName.includes(search)) return false;
      }
      if (from && loa.issue_date < from) return false;
      if (to && loa.issue_date > to) return false;
      return this.matchesSummaryFilter(loa, summaryFilter);
    });
  });

  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filteredLoas().length / this.pageSize())));
  protected readonly pagedLoas = computed(() => {
    const page = Math.min(this.currentPage(), this.totalPages());
    const start = (page - 1) * this.pageSize();
    return this.filteredLoas().slice(start, start + this.pageSize());
  });
  protected readonly startRow = computed(() => (this.filteredLoas().length === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1));
  protected readonly endRow = computed(() => Math.min(this.currentPage() * this.pageSize(), this.filteredLoas().length));
  protected readonly showReturnQuantumColumn = computed(() => {
    const mode = this.categoryMode();
    if (mode === 'Buy' || mode === 'Sell') return false;
    return this.filteredLoas().some((loa) => loa.category === 'Banking');
  });

  constructor() {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const summary = params.get('summary');
      const category = params.get('category');
      this.summaryFilter.set(this.isSummaryFilterMode(summary) ? summary : 'all');
      this.categoryMode.set(this.isFilterMode(category) ? category : this.defaultCategoryMode());
      this.currentPage.set(1);
    });
    this.load();
  }

  private defaultCategoryMode(): FilterMode {
    const role = (this.auth.user()?.role || '').toLowerCase();
    if (role === 'admin' || role === 'super_admin') return 'Sell';
    return 'all';
  }

  private load(): void {
    const generation = ++this.loadGeneration;
    this.pendingLoadTasks = 0;
    this.loading.set(true);

    this.beginLoadTask(generation);
    this.loaService.list().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (loas) => {
        if (generation !== this.loadGeneration) return;
        this.allLoas.set(loas || []);
        this.loadSchedules(loas || [], generation);
        this.endLoadTask(generation);
      },
      error: () => {
        if (generation !== this.loadGeneration) return;
        this.allLoas.set([]);
        this.scheduleMap.set({});
        this.toast.show('Failed to load LOAs', '', 'danger');
        this.endLoadTask(generation);
      },
    });

    this.beginLoadTask(generation);
    this.vendorsService
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (vendors) => {
          if (generation !== this.loadGeneration) return;
          this.vendors.set(vendors || []);
          this.endLoadTask(generation);
        },
        error: () => {
          if (generation !== this.loadGeneration) return;
          this.vendors.set([]);
          this.endLoadTask(generation);
        },
      });

    this.beginLoadTask(generation);
    this.contractsService
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (contracts) => {
          if (generation !== this.loadGeneration) return;
          this.contracts.set(contracts || []);
          this.endLoadTask(generation);
        },
        error: () => {
          if (generation !== this.loadGeneration) return;
          this.contracts.set([]);
          this.endLoadTask(generation);
        },
      });
  }

  private loadSchedules(loas: Loa[], generation: number): void {
    if (!loas.length) {
      this.scheduleMap.set({});
      return;
    }

    this.beginLoadTask(generation);
    forkJoin(
      loas.map((loa) =>
        this.loaService.listSchedules(loa.id).pipe(
          map((schedules) => [loa.id, schedules || []] as const),
          catchError(() => of([loa.id, []] as const)),
        ),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (entries) => {
          if (generation !== this.loadGeneration) return;
          this.scheduleMap.set(Object.fromEntries(entries));
          this.endLoadTask(generation);
        },
        error: () => {
          if (generation !== this.loadGeneration) return;
          this.scheduleMap.set({});
          this.endLoadTask(generation);
        },
      });
  }

  private beginLoadTask(generation: number): void {
    if (generation !== this.loadGeneration) return;
    this.pendingLoadTasks += 1;
    this.loading.set(true);
  }

  private endLoadTask(generation: number): void {
    if (generation !== this.loadGeneration) return;
    this.pendingLoadTasks = Math.max(0, this.pendingLoadTasks - 1);
    if (this.pendingLoadTasks === 0) {
      this.loading.set(false);
    }
  }

  private matchesSummaryFilter(loa: Loa, summaryFilter: SummaryFilterMode): boolean {
    if (summaryFilter === 'draft') return this.isDraftLoa(loa);
    if (summaryFilter === 'approved') return this.isApprovedLoa(loa);
    if (summaryFilter === 'pending') return this.isPendingApprovalLoa(loa);
    if (summaryFilter === 'pending_linking') return this.isPendingContractLinking(loa);
    return true;
  }

  private isPendingContractLinking(loa: Loa): boolean {
    return this.isApprovedLoa(loa) && !this.linkedLoaIds().has(loa.id);
  }

  protected setCategoryMode(mode: FilterMode): void {
    this.categoryMode.set(mode);
    this.currentPage.set(1);
  }

  protected applySummaryCategory(event: Event, label: string, mode: Exclude<FilterMode, 'all'>): void {
    event.stopPropagation();
    this.summaryFilter.set(SUMMARY_FILTER_BY_LABEL[label] || 'all');
    this.setCategoryMode(mode);
  }

  protected applySummaryTile(label: string): void {
    const nextFilter = SUMMARY_FILTER_BY_LABEL[label] || 'all';
    this.summaryFilter.set(nextFilter);
    if (nextFilter !== 'total') {
      this.categoryMode.set('all');
    }
    this.currentPage.set(1);
  }

  protected createLoa(category: Exclude<FilterMode, 'all'>): void {
    this.router.navigate(['/loas/new'], { queryParams: { category } });
  }

  protected removeLoa(loa: Loa): void {
    if (!this.canDeleteLoa(loa)) {
      this.toast.show(this.deleteTitle(loa), '', 'info');
      return;
    }
    if (this.deletingLoaId() === loa.id) return;
    if (!window.confirm(`Delete LOA ${loa.loa_number}?`)) return;

    this.deletingLoaId.set(loa.id);
    this.loaService.delete(loa.id).subscribe({
      next: () => {
        this.toast.show('LOA deleted', '', 'success');
        this.deletingLoaId.set(null);
        this.load();
      },
      error: (error) => {
        this.deletingLoaId.set(null);
        this.toast.show(error?.error?.detail || 'Failed to delete LOA', '', 'danger');
      },
    });
  }

  protected canDeleteLoa(loa: Loa): boolean {
    if (this.deletingLoaId() === loa.id) return false;
    if (typeof loa.can_delete === 'boolean') return loa.can_delete;
    return this.canEditLoa(loa);
  }

  protected deleteTitle(loa: Loa): string {
    if (this.deletingLoaId() === loa.id) return 'Deleting...';
    if (loa.can_delete === false) {
      return loa.delete_disabled_reason || 'LOA cannot be deleted';
    }
    if (!this.canEditLoa(loa)) return 'You are not authorized to delete this LOA';
    return 'Delete';
  }

  protected openVendorDetails(vendorId?: string | null): void {
    const id = (vendorId || '').trim();
    if (!id) return;
    const url = this.router.serializeUrl(this.router.createUrlTree(['/vendors', id]));
    window.open(url, '_blank');
  }

  protected exportLoas(): void {
    const headers = [
      'S.No.',
      'LOA Number',
      'LOA Type',
      'Entity',
      'State',
      'Period',
      'Time',
      'No. of Days',
      'Total Quantum (MWh)',
      'Region',
      'Approval Status',
      'LOA Status',
    ];
    if (this.showReturnQuantumColumn()) headers.splice(9, 0, 'Return Quantum (MWh)');
    const rows = this.filteredLoas().map((loa, index) => [
      index + 1,
      loa.loa_number,
      loa.category || '-',
      this.vendorMap()[loa.vendor_id] || 'Unknown',
      this.resolveState(loa),
      `${this.formatDate(this.periodStart(loa))} to ${this.formatDate(this.periodEnd(loa))}`,
      this.timeRange(loa),
      this.periodDayCount(loa),
      this.totalQuantumMwh(loa),
      this.resolveRegion(loa),
      this.approvalStatusLabel(loa.status),
      this.loaLifecycleStatus(loa),
    ]);
    if (this.showReturnQuantumColumn()) {
      rows.forEach((row, index) => row.splice(9, 0, this.returnQuantumMwh(this.filteredLoas()[index])));
    }
    const csv = [headers.join(','), ...rows.map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `loa_export_${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  protected applyFilters(): void {
    this.statusFilter.set(this.draftStatusFilter());
    this.searchQuery.set(this.draftSearch());
    this.fromDate.set(this.draftFromDate());
    this.toDate.set(this.draftToDate());
    this.currentPage.set(1);
  }

  protected clearFilters(): void {
    this.categoryMode.set('all');
    this.summaryFilter.set('all');
    this.draftStatusFilter.set('all');
    this.draftSearch.set('');
    this.draftFromDate.set('');
    this.draftToDate.set('');
    this.statusFilter.set('all');
    this.searchQuery.set('');
    this.fromDate.set('');
    this.toDate.set('');
    this.currentPage.set(1);
  }

  protected updateDraftSearch(value: string): void {
    this.draftSearch.set(value.trim());
  }

  protected updateDraftStatusFilter(value: string): void {
    this.draftStatusFilter.set(value || 'all');
  }

  protected updateDraftFromDate(value: string): void {
    this.draftFromDate.set(value);
  }

  protected updateDraftToDate(value: string): void {
    this.draftToDate.set(value);
  }

  protected setPageSize(value: string): void {
    this.pageSize.set(Number(value));
    this.currentPage.set(1);
  }

  protected goFirst(): void {
    this.currentPage.set(1);
  }

  protected goLast(): void {
    this.currentPage.set(this.totalPages());
  }

  protected periodStart(loa: Loa): string {
    const scheduledStart = this.scheduleBoundaryDate(loa, 'from_date', 'min');
    if (scheduledStart) return scheduledStart;
    return loa.effective_from || loa.issue_date;
  }

  protected periodEnd(loa: Loa): string {
    const scheduledEnd = this.scheduleBoundaryDate(loa, 'to_date', 'max');
    if (scheduledEnd) return scheduledEnd;
    return loa.effective_to || loa.issue_date;
  }

  protected timeRange(loa: Loa): string {
    const start = this.scheduleBoundaryTime(loa, 'from_time', 'min');
    const end = this.scheduleBoundaryTime(loa, 'to_time', 'max');
    if (!start && !end) return '-';
    if (!start) return end || '-';
    if (!end) return start;
    return `${start} to ${end}`;
  }

  protected formatDate(value?: string | null): string {
    if (!value) return '-';
    const [year, month, day] = value.split('T')[0].split('-');
    if (!year || !month || !day) return value;
    return `${day}-${month}-${year}`;
  }

  protected formatQuantum(value?: string | number | null): string {
    if (value === null || value === undefined || value === '') return '-';
    return `${value} MW`;
  }

  protected periodDayCount(loa: Loa): number | string {
    const schedules = this.schedulesFor(loa);
    if (schedules.length) {
      const coveredDays = new Set<string>();
      schedules.forEach((item) => {
        const start = this.parseDate(item.from_date);
        const end = this.parseDate(item.to_date);
        if (!start || !end || end < start) return;
        const cursor = new Date(start);
        while (cursor <= end) {
          coveredDays.add(cursor.toISOString().slice(0, 10));
          cursor.setDate(cursor.getDate() + 1);
        }
      });
      if (coveredDays.size) return coveredDays.size;
    }

    const start = this.parseDate(this.periodStart(loa));
    const end = this.parseDate(this.periodEnd(loa));
    if (!start || !end) return '-';
    const diff = Math.floor((end.getTime() - start.getTime()) / 86400000);
    return diff >= 0 ? diff + 1 : '-';
  }

  protected totalQuantumMwh(loa: Loa): string {
    const total = this.schedulesFor(loa).reduce((sum, item) => sum + this.scheduleQuantumMwh(item), 0);
    if (!Number.isFinite(total) || total <= 0) return '-';
    return `${this.formatDecimal(total)} MWh`;
  }

  protected returnQuantumMwh(loa: Loa): string {
    if (loa.category !== 'Banking') return '-';
    const total = this.schedulesFor(loa).reduce((sum, item) => sum + this.scheduleReturnQuantumMwh(item), 0);
    if (!Number.isFinite(total) || total <= 0) return '-';
    return `${this.formatDecimal(total)} MWh`;
  }

  protected canEditLoa(loa: Loa): boolean {
    if (this.isVendorUser()) return false;
    const role = this.userRoleKey();
    const isSuperAdmin = role === 'super_admin';
    const userId = this.auth.user()?.id || '';
    const isCreator = !!userId && (loa.created_by || '') === userId;
    return isSuperAdmin || isCreator;
  }

  

  protected deleteLoa(loa: Loa): void {
    if (!this.canDeleteLoa(loa)) {
      this.toast.show('LOA cannot be deleted because it is used in a contract', '', 'danger');
      return;
    }
    if (!confirm(`Delete LOA ${loa.loa_number}?`)) return;
    this.loaService.delete(loa.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toast.show('LOA deleted', '', 'success');
        this.load();
      },
      error: (error) => {
        const detail = error?.error?.detail || '';
        this.toast.show(detail || 'Failed to delete LOA', '', 'danger');
      },
    });
  }

  protected loaLifecycleStatus(loa: Loa): 'ACTIVE' | 'EXPIRED' {
    const end = this.parseDate(this.periodEnd(loa));
    if (!end) return 'ACTIVE';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return end < today ? 'EXPIRED' : 'ACTIVE';
  }

  protected segmentWidth(value: number, total: number): number {
    if (!total) return 0;
    return (value / total) * 100;
  }

  protected regionFor(state?: string | null): string {
    if (!state) return '-';
    return STATE_REGION_MAP[state] || '-';
  }

  protected resolveState(loa: Loa): string {
    return loa.state || this.vendorDetailsMap()[loa.vendor_id]?.state || '-';
  }

  protected resolveRegion(loa: Loa): string {
    return this.regionFor(this.resolveState(loa));
  }

  protected getStatusLabel(status?: string | null): string {
    return (status || '-')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  protected approvalStatusLabel(status?: string | null): string {
    const normalized = (status || '').toLowerCase().trim();
    if (normalized === 'final_approved') return 'Approved';
    if (normalized === 'pending') return 'Draft';
    return this.getStatusLabel(status);
  }

  protected normalizedLoaWorkflowStatus(loa: Loa): string {
    return (loa.loa_status || loa.status || '').toLowerCase().trim();
  }

  protected isDraftLoa(loa: Loa): boolean {
    return this.normalizedLoaWorkflowStatus(loa) === 'draft';
  }

  protected isApprovedLoa(loa: Loa): boolean {
    const status = this.normalizedLoaWorkflowStatus(loa);
    return ['approved', 'final_approved', 'active'].includes(status);
  }

  protected isPendingApprovalLoa(loa: Loa): boolean {
    const status = this.normalizedLoaWorkflowStatus(loa);
    return ['pending_initial_approval', 'pending_accounts_approval', 'pending_operation_approval'].includes(status);
  }

  private makeTile(label: string, loas: Loa[], icon: any) {
    return {
      label,
      count: loas.length,
      sell: loas.filter((item) => item.category === 'Sell').length,
      buy: loas.filter((item) => item.category === 'Buy').length,
      banking: loas.filter((item) => item.category === 'Banking').length,
      icon,
    };
  }

  private parseDate(value?: string | null): Date | null {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private schedulesFor(loa: Loa): LoaSchedule[] {
    return this.scheduleMap()[loa.id] || [];
  }

  private scheduleQuantumMwh(schedule: LoaSchedule): number {
    const quantum = Number(schedule.quantum_mw ?? 0);
    const dayCount = this.scheduleDayCount(schedule);
    const hours = this.scheduleDurationHours(schedule);
    if (!Number.isFinite(quantum) || quantum <= 0 || dayCount <= 0 || hours <= 0) return 0;
    return quantum * dayCount * hours;
  }

  private scheduleReturnQuantumMwh(schedule: LoaSchedule): number {
    const totalQuantum = this.scheduleQuantumMwh(schedule);
    const returnPercentage = Number(schedule.return_percentage ?? 0);
    if (!Number.isFinite(totalQuantum) || totalQuantum <= 0) return 0;
    if (!Number.isFinite(returnPercentage) || returnPercentage <= 0) return 0;
    return totalQuantum * (returnPercentage / 100);
  }

  private scheduleDayCount(schedule: LoaSchedule): number {
    const start = this.parseDate(schedule.from_date);
    const end = this.parseDate(schedule.to_date);
    if (!start || !end) return 0;
    const diff = Math.floor((end.getTime() - start.getTime()) / 86400000);
    return diff >= 0 ? diff + 1 : 0;
  }

  private scheduleDurationHours(schedule: LoaSchedule): number {
    const start = this.timeSortValue(schedule.from_time || '');
    const end = this.timeSortValue(schedule.to_time || '');
    if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
    const diffMinutes = end - start;
    return diffMinutes > 0 ? diffMinutes / 60 : 0;
  }

  private scheduleBoundaryDate(loa: Loa, key: 'from_date' | 'to_date', mode: 'min' | 'max'): string | null {
    const values = this.schedulesFor(loa)
      .map((item) => item[key])
      .filter((value): value is string => !!value)
      .sort();
    if (!values.length) return null;
    return mode === 'min' ? values[0] : values[values.length - 1];
  }

  private scheduleBoundaryTime(loa: Loa, key: 'from_time' | 'to_time', mode: 'min' | 'max'): string | null {
    const values = this.schedulesFor(loa)
      .map((item) => item[key])
      .filter((value): value is string => !!value)
      .sort((left, right) => this.timeSortValue(left) - this.timeSortValue(right));
    if (!values.length) return null;
    return mode === 'min' ? values[0] : values[values.length - 1];
  }

  private timeSortValue(value: string): number {
    const [hours = '0', minutes = '0'] = value.split(':');
    const hourValue = Number(hours);
    const minuteValue = Number(minutes);
    if (!Number.isFinite(hourValue) || !Number.isFinite(minuteValue)) return Number.NaN;
    return hourValue * 60 + minuteValue;
  }

  private formatDecimal(value: number): string {
    const rounded = Math.round((value + Number.EPSILON) * 100) / 100;
    return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(2);
  }

  private isFilterMode(value: string | null): value is FilterMode {
    return value === 'all' || value === 'Buy' || value === 'Sell' || value === 'Banking';
  }

  private isSummaryFilterMode(value: string | null): value is SummaryFilterMode {
    return value === 'all' || value === 'total' || value === 'draft' || value === 'approved' || value === 'pending_linking' || value === 'pending';
  }

}
